/**
 * @file slider.tsx
 * @description Ползунок на base-ui (`@base-ui/react/slider`) под Commora Console
 * (phase19.16, партия SB120 «свобода выбора», этап 0; site-builder.md SB120;
 * проект docs/phases/phase19.16-materials/uiux-sb120-fields.md §2).
 *
 * Тонкая обёртка, как остальные `ui/*.tsx`: одно значение (не диапазон),
 * дорожка 6 px на подложке, заполненная часть цветом `primary`, бегунок 20 px
 * с зоной касания 48 × 48 (псевдоэлемент), кольцо фокуса с клавиатуры.
 * Клавиатура — base-ui: ←/→ шаг, Shift и PageUp/PageDown — `largeStep`,
 * Home/End — края.
 *
 *  - `inherited` — значение не задано, показано наследуемое: бегунок полый,
 *    заполнение бледное (`data-inherited` на корне);
 *  - `marker` — метка ┊ «по умолчанию» на дорожке (title — подпись).
 *
 * ```tsx
 * <Slider value={24} min={0} max={200} onValueChange={preview} onValueCommitted={save}
 *   thumbProps={{ "aria-labelledby": labelId }} />
 * ```
 */

import { Slider as SliderPrimitive } from "@base-ui/react/slider";

import { cn } from "@/lib/utils";

/** Подписи бегунка для скринридера — уходят во вложенный `input type="range"`. */
export interface SliderThumbAria {
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  /** «24 пикселя» / «16 пикселей, как у колонки». */
  getAriaValueText?: (value: number) => string;
}

export interface SliderProps {
  value: number;
  min: number;
  max: number;
  step?: number;
  largeStep?: number;
  disabled?: boolean;
  /** Наследуемое значение: бегунок полый. */
  inherited?: boolean;
  /** Метка ┊ на дорожке (значение в единицах ползунка). */
  marker?: { value: number; label: string };
  /** Во время перетаскивания и с клавиатуры. */
  onValueChange?: (value: number) => void;
  /** Отпустили бегунок / нажали клавишу — значение «записано». */
  onValueCommitted?: (value: number) => void;
  thumbProps?: SliderThumbAria;
  className?: string;
}

/** Доля пути по дорожке, 0–100 %. */
const percentOf = (value: number, min: number, max: number) =>
  max === min ? 0 : Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));

export function Slider({
  value,
  min,
  max,
  step = 1,
  largeStep,
  disabled,
  inherited = false,
  marker,
  onValueChange,
  onValueCommitted,
  thumbProps,
  className,
}: SliderProps) {
  const { getAriaValueText, ...aria } = thumbProps ?? {};
  return (
    <SliderPrimitive.Root
      value={value}
      min={min}
      max={max}
      step={step}
      largeStep={largeStep ?? step * 10}
      disabled={disabled}
      onValueChange={(next) => onValueChange?.(next)}
      onValueCommitted={(next) => onValueCommitted?.(next)}
      data-slot="slider"
      data-inherited={inherited ? "" : undefined}
      className={cn("group/slider w-full min-w-0", className)}
    >
      {/* Высота 48 — по ней ловится касание дорожки; сама дорожка тонкая. */}
      <SliderPrimitive.Control className="relative flex h-12 w-full touch-none items-center select-none data-[disabled]:opacity-50">
        <SliderPrimitive.Track className="relative h-1.5 w-full rounded-full bg-surface-low [box-shadow:inset_0_0_0_1px_var(--outline-variant)]">
          <SliderPrimitive.Indicator className="rounded-full bg-primary group-data-[inherited]/slider:bg-on-surface-variant/40" />
          {marker ? (
            <span
              aria-hidden
              title={marker.label}
              data-slot="slider-marker"
              className="pointer-events-none absolute top-1/2 h-4 w-0 -translate-y-1/2 border-l-2 border-dotted border-on-surface-variant"
              style={{ left: `${percentOf(marker.value, min, max)}%` }}
            />
          ) : null}
          <SliderPrimitive.Thumb
            {...aria}
            getAriaValueText={getAriaValueText ? (_formatted, v) => getAriaValueText(v) : undefined}
            data-slot="slider-thumb"
            className={cn(
              // Зона касания 48 × 48 вокруг бегунка 20 px — псевдоэлемент.
              "relative size-5 rounded-full border-2 border-primary bg-surface-lowest shadow-sm outline-none",
              "before:absolute before:-inset-3.5 before:content-['']",
              "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-background",
              "data-[dragging]:scale-110 transition-transform motion-reduce:transition-none",
              // Наследуемое: полый бегунок — белая середина, серый пунктир, без тени.
              "group-data-[inherited]/slider:border-dashed group-data-[inherited]/slider:border-on-surface-variant group-data-[inherited]/slider:shadow-none",
            )}
          />
        </SliderPrimitive.Track>
      </SliderPrimitive.Control>
    </SliderPrimitive.Root>
  );
}
